import React from 'react';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { PromiseStatus, PromiseSector } from '@/data/promises';

export interface FilterOptions {
  politician: string | null;
  politicalParty: string | null;
  status: PromiseStatus | null;
  sector: PromiseSector | null;
  year: string | null;
}

interface FilterSectionProps {
  filters: FilterOptions;
  onFilterChange: (key: keyof FilterOptions, value: string | null) => void;
  onClearFilters: () => void;
  politicians: string[];
  parties: string[];
  sectors: PromiseSector[];
  years: string[];
}

const FilterSection = ({
  filters,
  onFilterChange,
  onClearFilters,
  politicians,
  parties,
  sectors,
  years
}: FilterSectionProps) => {
  const hasActiveFilters = Object.values(filters).some(Boolean);

  return (
    <div className="flex flex-col space-y-4">
      {hasActiveFilters && (
        <Button 
          variant="outline" 
          size="sm" 
          onClick={onClearFilters}
          className="flex items-center gap-2 w-full"
        >
          <X className="h-4 w-4" />
          Clear Filters
        </Button>
      )}

      <div className="space-y-1">
        <Label htmlFor="politician-filter">Politician</Label>
        <Select
          value={filters.politician || 'all'}
          onValueChange={(value) => onFilterChange('politician', value === 'all' ? null : value)}
        >
          <SelectTrigger id="politician-filter" className="w-full">
            <SelectValue placeholder="All Politicians" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Politicians</SelectItem>
            {politicians.map((politician) => (
              <SelectItem key={politician} value={politician}>
                {politician}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1">
        <Label htmlFor="party-filter">Political Party</Label>
        <Select
          value={filters.politicalParty || 'all'}
          onValueChange={(value) => onFilterChange('politicalParty', value === 'all' ? null : value)}
        >
          <SelectTrigger id="party-filter" className="w-full">
            <SelectValue placeholder="All Parties" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Parties</SelectItem>
            {parties.map((party) => (
              <SelectItem key={party} value={party}>
                {party}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1">
        <Label htmlFor="status-filter">Status</Label>
        <Select
          value={filters.status || 'all'}
          onValueChange={(value) => onFilterChange('status', value === 'all' ? null : value as PromiseStatus)}
        >
          <SelectTrigger id="status-filter" className="w-full">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="fulfilled">Fulfilled</SelectItem>
            <SelectItem value="ongoing">Ongoing</SelectItem>
            <SelectItem value="broken">Broken</SelectItem>
            <SelectItem value="unknown">Unknown</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1">
        <Label htmlFor="sector-filter">Sector</Label>
        <Select
          value={filters.sector || 'all'}
          onValueChange={(value) => onFilterChange('sector', value === 'all' ? null : value as PromiseSector)}
        >
          <SelectTrigger id="sector-filter" className="w-full">
            <SelectValue placeholder="All Sectors" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sectors</SelectItem>
            {sectors.map((sector) => (
              <SelectItem key={sector} value={sector}>
                {sector}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1">
        <Label htmlFor="year-filter">Year Made</Label>
        <Select
          value={filters.year || 'all'}
          onValueChange={(value) => onFilterChange('year', value === 'all' ? null : value)}
        >
          <SelectTrigger id="year-filter" className="w-full">
            <SelectValue placeholder="All Years" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Years</SelectItem>
            {years.map((year) => (
              <SelectItem key={year} value={year}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default FilterSection;
